import { tilesOf } from '@casa/model';
import { RtspStream, type PipelineMode, type StreamStats } from '@casa/video';
import { bridge } from '../bridge/native_bridge.ts';
import { readCachedDashboard } from '../data/dashboard.ts';

// Spike-only scaffolding for iterations I2 and I3 (thrown away at the gate): one RTSP stream on
// one canvas, driven from the spike page or over DevTools by scripts/video-report.mjs.

/** The camera of the first video tile in the owner's dashboard (camera C1 of docs/reference-devices.md). */
export async function defaultCameraUrl() : Promise<string | undefined> {
    if (!bridge) {
        return undefined;
    }

    const dashboard = await readCachedDashboard(bridge);
    for (const screen of dashboard?.screens ?? []) {
        for (const tile of tilesOf(screen)) {
            if (tile.type === 'video' && 'url' in tile.options) {
                return tile.options.url;
            }
        }
    }

    return undefined;
}

export type VideoSpikeMode = PipelineMode | 'auto';

export interface VideoSpikeOptions {
    url?: string;
    mode?: VideoSpikeMode;
    /** Watchdog timeout; shortened by the stall test. */
    stallTimeoutMs?: number;
}

export interface VideoSpikeState {
    /** `idle`, `error` (the stream could not be created) or the state of the stream. */
    status: 'idle' | 'error' | StreamStats['state'];
    error: string | null;
    /** Kept after the stream was stopped, so the last numbers stay readable. */
    stats: StreamStats | null;
}

let host: HTMLElement | null = null;
let stream: RtspStream | null = null;
let error: string | null = null;
let lastStats: StreamStats | null = null;

export const videoSpike = {
    /** The element that gets the canvas. */
    setHost(element: HTMLElement | null) : void {
        host = element;
    },

    /** Resolves when the first connection attempt has reached PLAY or failed; the stream retries by itself. */
    async start(options: VideoSpikeOptions = {}) : Promise<VideoSpikeState> {
        await videoSpike.stop();
        lastStats = null;
        error = null;
        try {
            const net = bridge?.net;
            if (!net) {
                throw new Error('no native bridge in this host');
            }
            if (!host) {
                throw new Error('the spike page is not shown');
            }
            // A fresh canvas for every start: a Worker pipeline takes its canvas over for good.
            const canvas = document.createElement('canvas');
            canvas.width = 1024;
            canvas.height = 576;
            canvas.style.cssText = 'width: 100%; height: auto; display: block';
            host.replaceChildren(canvas);
            const url = options.url ?? (await defaultCameraUrl());
            if (!url) {
                throw new Error('no camera URL given and no video tile in the dashboard');
            }

            const created = new RtspStream({
                url,
                openChannel: (address, port) => net.tcp.open(address, port),
                canvas,
                ...(options.mode ? { mode: options.mode } : {}),
                ...(options.stallTimeoutMs ? { stallTimeoutMs: options.stallTimeoutMs } : {}),
                log: (line) => console.warn(line),
            });
            stream = created;
            await created.start();
        } catch (startError) {
            error = startError instanceof Error ? startError.message : String(startError);
        }
        return videoSpike.state();
    },

    async stop() : Promise<void> {
        const current = stream;
        stream = null;
        error = null;
        if (current) {
            await current.stop();
            lastStats = current.stats();
        }
    },

    state() : VideoSpikeState {
        if (stream) {
            lastStats = stream.stats();
        }
        if (error !== null) {
            return { status: 'error', error, stats: lastStats };
        }

        return { status: stream ? lastStats!.state : 'idle', error: lastStats?.lastError ?? null, stats: lastStats };
    },
};
